import { put, select } from 'redux-saga/effects';
import { signalingContentPushed } from 'features/layout/features/content/actions';
import {
  gridPanelOpened,
  lockedContentChanged,
  selectGridPanel,
  selectMaximizedStream,
} from 'features/layout/features/content/contentSlice';
import { streamMinimized } from 'features/layout/actions';
import { selectContentWidth } from 'features/layout/features/contentDimensions/contentDimensionsSlice';
import { GridPanelName } from 'features/layout/features/content/types';
import { fileViewed, whiteboardViewed } from 'features/content-library/actions';
import { screenshareSelected } from 'features/screenshare/actions';
import { selectScreenshareStreams } from 'features/layout/features/order/orderSlice';
import { SourceDetails } from 'features/layout/types';
import {
  currentActiveFileUpdated,
  previewFileClosed,
  selectPreviewedFile,
} from 'features/content-library/contentLibrarySlice';
import { ContentLibraryOpenedFile } from 'features/content-library/types';
import { panelMap } from 'features/layout/features/content/constants';
import { remoteZoomSynced } from 'features/content-zoom/contentZoomSlice';
import { buildZoomId, fromServerScale } from 'features/content-zoom/utils/formatUtils';
import { LOCKED_ZOOM_CONTENT_TYPES } from 'features/content-zoom/constants';
import { selectLocalUserId } from 'features/users/usersSlice';
import { UserId } from 'features/users/types';

export function* onContentPushedSaga(action: ReturnType<typeof signalingContentPushed>) {
  const {
    data: { type, id, page, url, lock, scale, offsetX, offsetY },
    initiator,
  } = action.payload;

  const maximizedStream: SourceDetails | null = yield select(selectMaximizedStream);
  const contentWidth: number = yield select(selectContentWidth);

  if (maximizedStream && contentWidth === 1) {
    yield put(streamMinimized());
  }

  const currentPanel: GridPanelName = yield select(selectGridPanel);
  const nextPanel: GridPanelName = panelMap[type];

  if (nextPanel !== currentPanel) {
    yield put(gridPanelOpened(nextPanel));
  }

  if (!lock) {
    yield put(lockedContentChanged(null));
  }

  if (type === 'screenshare') {
    const screenShares: (SourceDetails & { feedId: string })[] =
      yield select(selectScreenshareStreams);

    const screenShare = screenShares.find((item) => item.feedId === id);

    if (screenShare) {
      if (lock) {
        yield put(lockedContentChanged({ type, id: screenShare.feedId }));
      }

      yield put(
        screenshareSelected({
          feedId: screenShare.feedId,
          userId: screenShare.userId,
          update: true,
        })
      );
    }
  } else if (type === 'whiteboard') {
    if (lock) {
      yield put(lockedContentChanged({ type, id }));
    }

    yield put(whiteboardViewed(id));
  } else if (type === 'file') {
    let data;

    if (page !== undefined || url) {
      data = { page, url };
    }

    if (lock) {
      yield put(lockedContentChanged({ type, id, ...data }));
    }

    yield put(fileViewed({ id, data }));

    yield put(currentActiveFileUpdated(id));
  }

  if (lock && LOCKED_ZOOM_CONTENT_TYPES.includes(type)) {
    yield put(
      remoteZoomSynced({
        zoomId: buildZoomId(id, page),
        scale: fromServerScale(scale),
        offsetX: offsetX ?? 0,
        offsetY: offsetY ?? 0,
      })
    );
  }

  const previewedFile: ContentLibraryOpenedFile | null = yield select(selectPreviewedFile);
  if (previewedFile) {
    const localUserId: UserId = yield select(selectLocalUserId);
    if (localUserId === initiator?.id) {
      yield put(previewFileClosed());
    }
  }
}
